import { API_BASE_URL as BASE } from './apiConfig';
import { createAuthedAxios } from '@/lib/authedAxios';

const API_BASE_URL = `${BASE}/api/monitored-sites`;

const client = createAuthedAxios();

export type CrawlMode = 'SINGLE_PAGE' | 'DOMAIN_WIDE';
export type IntervalType = 'DAILY' | 'WEEKLY';
export type MonitoredSiteStatus = 'ACTIVE' | 'PAUSED' | 'FAILED';

export interface MonitoredSite {
  id: number;
  url: string;
  crawlMode: CrawlMode;
  maxDepth: number;
  maxPages: number;
  intervalType: IntervalType;
  status: MonitoredSiteStatus;
  lastCrawledAt: string | null;
  consecutiveFailureCount: number;
  createdAt: string;
}

export interface MonitoredSiteRegisterInput {
  url: string;
  crawlMode?: CrawlMode;
  maxDepth?: number;
  maxPages?: number;
  intervalType: IntervalType;
}

export interface MonitoredSiteBulkInput {
  urls: string[];
  crawlMode?: CrawlMode;
  maxDepth?: number;
  maxPages?: number;
  intervalType: IntervalType;
}

export interface MonitoredSiteBulkRowError {
  rowNumber: number;
  url: string;
  reason: string;
}

export interface MonitoredSiteBulkResult {
  totalCount: number;
  successCount: number;
  failureCount: number;
  registered: MonitoredSite[];
  errors: MonitoredSiteBulkRowError[];
}

export const monitoredSiteApi = {
  list: async (): Promise<MonitoredSite[]> => {
    const response = await client.get<MonitoredSite[]>(API_BASE_URL);
    return response.data;
  },

  register: async (input: MonitoredSiteRegisterInput): Promise<MonitoredSite> => {
    const response = await client.post<MonitoredSite>(API_BASE_URL, input);
    return response.data;
  },

  registerBulk: async (input: MonitoredSiteBulkInput): Promise<MonitoredSiteBulkResult> => {
    const response = await client.post<MonitoredSiteBulkResult>(`${API_BASE_URL}/bulk`, input);
    return response.data;
  },

  pause: async (id: number): Promise<MonitoredSite> => {
    const response = await client.patch<MonitoredSite>(`${API_BASE_URL}/${id}/pause`);
    return response.data;
  },

  resume: async (id: number): Promise<MonitoredSite> => {
    const response = await client.patch<MonitoredSite>(`${API_BASE_URL}/${id}/resume`);
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await client.delete(`${API_BASE_URL}/${id}`);
  },

  runNow: async (id: number): Promise<MonitoredSite> => {
    const response = await client.post<MonitoredSite>(`${API_BASE_URL}/${id}/run-now`);
    return response.data;
  },
};
